import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid,
  Card,
  CardActionArea,
  CardContent,
  FormControl,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Button,
  TextField,
  IconButton,
  CardActions,
  Collapse,
} from '@mui/material';
import { SketchPicker } from 'react-color';
import { useDesign } from '../../context/DesignContext';
import tokens from '../../theme/tokens';
import DeleteIcon from '@mui/icons-material/Delete';
import CollapsibleSectionHeader from '../shared/CollapsibleSectionHeader';
import { useLanguage } from '../../localization';
import { getTranslatedDesignName } from '../../designs/DesignRegistry';

// Color Donut Component - A visual circular representation of the color scheme
const ColorDonutComponent = ({ colors, size = 60 }) => {
  // Extract main colors from the design, ensure fallbacks if colors are missing
  const mainColors = [
    colors?.primary || tokens.colors.neutral[300],
    colors?.secondary || tokens.colors.neutral[400],
    colors?.tertiary || tokens.colors.neutral[500],
    colors?.quaternary || tokens.colors.neutral[600],
    colors?.accent1 || tokens.colors.neutral[700]
  ].filter(Boolean); // Filter out any potentially undefined colors

  if (mainColors.length === 0) {
      // Fallback if no colors are defined
      return <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: tokens.colors.neutral[200] }} />;
  }

  const segmentCount = mainColors.length;
  const centerSize = size * 0.3; // Size of inner circle
  const strokeWidth = (size - centerSize) / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <Box sx={{
      width: size,
      height: size,
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 'auto', // Center the donut horizontally
      mb: tokens.spacing[2], // Add some margin below the donut
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}> {/* Rotate to start from top */}
        {mainColors.map((color, index) => {
          const arcLength = circumference / segmentCount;
          // Calculate offset for this segment
          const offset = index * arcLength;

          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${arcLength} ${circumference - arcLength}`}
              strokeDashoffset={-offset} // Apply negative offset
            />
          );
        })}
        {/* Inner circle with background color */}
        <circle
          cx={size/2}
          cy={size/2}
          r={centerSize/2}
          fill={colors?.background || tokens.colors.background.paper}
          stroke={colors?.border || tokens.colors.borders.default}
          strokeWidth="1"
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center center' }} // Counter-rotate inner elements if needed
        />
      </svg>
       {/* Text removed as it might overlap weirdly with segments */}
    </Box>
  );
};
const ColorDonut = React.memo(ColorDonutComponent);

// --- Color Picker Swatch Component ---
const ColorPickerSwatchComponent = ({ label, color, onChange }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);

  // Helper function to normalize color for the picker
  const normalizeColorForPicker = (colorValue) => {
    if (!colorValue) return '#000000';
    
    // If it's already an rgba/rgb string, return as is (SketchPicker can handle it)
    if (colorValue.startsWith('rgba(') || colorValue.startsWith('rgb(')) {
      return colorValue;
    }
    
    // If it's a hex value, return as is
    if (colorValue.startsWith('#')) {
      return colorValue;
    }
    
    // Fallback for any other format
    return colorValue;
  };

  // Memoize handlers
  const handleClick = useCallback(() => setDisplayColorPicker(!displayColorPicker), [displayColorPicker]);
  const handleClose = useCallback(() => setDisplayColorPicker(false), []);
  const handleChange = useCallback((colorResult) => {
    // Use rgba format to support opacity/alpha
    const { r, g, b, a } = colorResult.rgb;
    const rgbaString = `rgba(${r}, ${g}, ${b}, ${a})`;
    onChange(rgbaString);
  }, [onChange]);

  const normalizedColor = normalizeColorForPicker(color);

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        title={label}
        placement="top"
        arrow
      >
        <Button
          variant="outlined"
          onClick={handleClick} // Use memoized handler
          sx={{
            minWidth: tokens.spacing[10],
            height: tokens.spacing[10],
            padding: tokens.spacing[1],
            borderColor: tokens.colors.borders.default,
            '&:hover': {
              borderColor: tokens.colors.primary.main,
            }
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: color, // Use original color for display
              borderRadius: tokens.radius.sm,
            }}
          />
        </Button>
      </Tooltip>
      {displayColorPicker ? (
        <Box sx={{ position: 'absolute', zIndex: tokens.zIndex.dropdown, top: '100%', left: 0, mt: tokens.spacing[1] }}>
          <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }} onClick={handleClose} /* Use memoized handler */ />
          <SketchPicker color={normalizedColor} onChange={handleChange} /* Use memoized handler */ />
        </Box>
      ) : null}
    </Box>
  );
};
const ColorPickerSwatch = React.memo(ColorPickerSwatchComponent);
// --- END Color Picker Swatch ---

// --- Define which colors are editable in the custom scheme ---
const EDITABLE_COLOR_KEYS = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'tertiary', label: 'Tertiary' },
  { key: 'quaternary', label: 'Quaternary' },
  { key: 'sidebarPrimary', label: 'Sidebar Primary' },
  { key: 'accent1', label: 'Accent 1' },
  { key: 'accent2', label: 'Accent 2' },
  { key: 'text', label: 'Text' },
  { key: 'textSecondary', label: 'Text Secondary' },
  { key: 'background', label: 'Background' },
  { key: 'sidebarBackground', label: 'Sidebar BG' },
  { key: 'sidebarText', label: 'Sidebar Text' },
  { key: 'border', label: 'Border' },
];

const DesignSelectorComponent = () => {
  const {
    selectedDesign,
    setSelectedDesign,
    customColorScheme,
    setCustomColor,
    setBaseForCustomScheme,
    lastStandardDesignId,
    savedCustomSchemes,
    saveCustomScheme,
    loadCustomScheme,
    deleteCustomScheme,
    designs,
  } = useDesign();
  
  const { t } = useLanguage();
  
  const availableDesigns = useMemo(() => designs.filter(d => d.id !== 'custom'), [designs]);

  // --- State for collapsible sections ---
  const [colorSchemesOpen, setColorSchemesOpen] = useState(true);
  const [customControlsOpen, setCustomControlsOpen] = useState(true);
  const [savedSchemesOpen, setSavedSchemesOpen] = useState(true);

  // --- State for design categories ---
  const [openDesignCategories, setOpenDesignCategories] = useState({
    'default': true,
    'tech': true,
    'professional': true,
    'other': true,
  });

  // --- State for Custom category collapse ---
  const [customCategoryOpen, setCustomCategoryOpen] = useState(true);

  const [saveSchemeName, setSaveSchemeName] = useState('');
  const [selectedBaseId, setSelectedBaseId] = useState(lastStandardDesignId || availableDesigns[0]?.id || 'default-black');

  const designsByCategory = useMemo(() => {
    return availableDesigns.reduce((acc, design) => {
      const category = design.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(design);
      return acc;
    }, {});
  }, [availableDesigns]);
  const categoryOrder = ['default', 'professional', 'tech', 'other'];

  useEffect(() => {
      if (lastStandardDesignId !== selectedBaseId) {
          setSelectedBaseId(lastStandardDesignId);
      }
  }, [lastStandardDesignId]);

  const handleDesignChange = (designId) => {
    setSelectedDesign(designId);
  };

  const handleBaseSelectChange = (event) => {
      const baseId = event.target.value;
      setSelectedBaseId(baseId);
      setBaseForCustomScheme(baseId);
  };

  const handleSaveScheme = () => {
    saveCustomScheme(saveSchemeName);
    setSaveSchemeName('');
  };

  // --- Toggle function for design categories ---
  const toggleDesignCategory = useCallback((category) => {
    setOpenDesignCategories(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);

  // --- Toggle function for Custom category ---
  const toggleCustomCategory = useCallback(() => {
    setCustomCategoryOpen(prev => !prev);
  }, []);

  return (
    <Box>
      {/* --- Color Schemes Section --- */}
      <CollapsibleSectionHeader
        title={t('settingsPanel.design.colorSchemes')}
        isOpen={colorSchemesOpen}
        toggleOpen={() => setColorSchemesOpen(!colorSchemesOpen)}
        sx={{ mb: 2 }}
      />
      <Collapse in={colorSchemesOpen}>
        <Box sx={{ pl: 2, pr: 2 }}>
          {/* Standard Designs by Category */}
          {categoryOrder.map(category => designsByCategory[category] && (
            <Box key={category} sx={{ mb: tokens.spacing[4] }}>
              <CollapsibleSectionHeader
                title={t(`settingsPanel.design.categories.${category}`)}
                isOpen={!!openDesignCategories[category]}
                toggleOpen={() => toggleDesignCategory(category)}
                isCategory={true}
              />
              <Collapse in={!!openDesignCategories[category]}>
                <Grid container spacing={tokens.spacing[3]} sx={{ pt: tokens.spacing[2] }}>
                  {designsByCategory[category].map((design) => (
                    <Grid item xs={6} sm={4} key={design.id}>
                      <Tooltip
                        title={design.description || design.name}
                        placement="top"
                        arrow
                      >
                        <Card
                          variant="outlined"
                          sx={{
                            border: `1px solid ${selectedDesign === design.id ? tokens.colors.primary.main : tokens.colors.borders.default}`,
                            borderWidth: selectedDesign === design.id ? '2px' : '1px',
                            transition: `border-color ${tokens.transitions.durations.quick} ease`,
                            height: '100%', display: 'flex', flexDirection: 'column',
                            borderRadius: tokens.radius.base,
                            boxShadow: selectedDesign === design.id ? tokens.shadows.sm : 'none',
                            '&:hover': { 
                               borderColor: selectedDesign !== design.id ? tokens.colors.primary.light : tokens.colors.primary.main,
                               boxShadow: tokens.shadows.md,
                            }
                          }}
                        >
                          <CardActionArea onClick={() => handleDesignChange(design.id)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: tokens.spacing[2] }}>
                            <ColorDonut colors={design.colors} />
                            <CardContent sx={{ textAlign: 'center', p: 0, pt: tokens.spacing[2] }}>
                              <Typography variant="body2" component="div">
                                {getTranslatedDesignName(design.id, t)}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          ))}

          {/* Custom Scheme Card */}
          <Box sx={{ mb: tokens.spacing[4] }}>
             <CollapsibleSectionHeader
               title={t('settingsPanel.design.categories.custom')}
               isOpen={customCategoryOpen}
               toggleOpen={toggleCustomCategory}
               isCategory={true}
             />
             <Collapse in={customCategoryOpen}>
               <Grid container spacing={tokens.spacing[3]} sx={{ pt: tokens.spacing[2] }}>
                   <Grid item xs={6} sm={4}>
                        <Card
                           variant="outlined"
                           sx={{
                             border: `1px solid ${selectedDesign === 'custom' ? tokens.colors.primary.main : tokens.colors.borders.default}`,
                             borderWidth: selectedDesign === 'custom' ? '2px' : '1px',
                             transition: `border-color ${tokens.transitions.durations.quick} ease`,
                             height: '100%', display: 'flex', flexDirection: 'column',
                             borderRadius: tokens.radius.base,
                             boxShadow: selectedDesign === 'custom' ? tokens.shadows.sm : 'none',
                             '&:hover': {
                                borderColor: selectedDesign !== 'custom' ? tokens.colors.primary.light : tokens.colors.primary.main,
                                boxShadow: tokens.shadows.md,
                             }
                           }}
                         >
                           <CardActionArea onClick={() => handleDesignChange('custom')} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: tokens.spacing[2] }}>
                             <ColorDonut colors={customColorScheme} />
                             <CardContent sx={{ textAlign: 'center', p: 0, pt: tokens.spacing[2] }}>
                               <Typography variant="body2" component="div">
                                 {t('settingsPanel.design.editCustom')}
                               </Typography>
                             </CardContent>
                           </CardActionArea>
                         </Card>
                   </Grid>
               </Grid>
             </Collapse>
           </Box>
        </Box>
      </Collapse>
      {/* --- End Color Schemes Section --- */}


      {/* --- Customization Controls (Conditional & Collapsible) --- */}
      {selectedDesign === 'custom' && (
        <Box sx={{ mb: tokens.spacing[6] }}>
          {/* Use the shared component */}
          <CollapsibleSectionHeader
            title={t('settingsPanel.design.customControls')}
            isOpen={customControlsOpen}
            toggleOpen={() => setCustomControlsOpen(!customControlsOpen)}
            // isCategory={false} // Default is false
          />
          <Collapse in={customControlsOpen}>
            <Box sx={{ p: tokens.spacing[3], border: `1px dashed ${tokens.colors.borders.default}`, borderRadius: tokens.radius.base, mt: tokens.spacing[1] }}>
                <FormControl fullWidth size="small" sx={{ mb: tokens.spacing[4] }}>
                    <InputLabel id="base-design-select-label">{t('settingsPanel.design.baseDesign')}</InputLabel>
                    <Select
                        labelId="base-design-select-label"
                        id="base-design-select"
                        value={selectedBaseId}
                        label={t('settingsPanel.design.baseDesign')}
                        onChange={handleBaseSelectChange}
                        sx={{ borderRadius: tokens.radius.sm }}
                    >
                        {availableDesigns.map(design => (
                            <MenuItem key={design.id} value={design.id}>{getTranslatedDesignName(design.id, t)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="overline" display="block" gutterBottom sx={{ color: tokens.colors.text.secondary, fontSize: tokens.typography.fontSizes.xs }}>{t('settingsPanel.design.editColors')}:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing[4] }}>
                    {EDITABLE_COLOR_KEYS.map(({ key, label }) => (
                        <ColorPickerSwatch
                            key={key}
                            label={label}
                            color={customColorScheme[key] || tokens.colors.background.paper}
                            onChange={(color) => setCustomColor(key, color)}
                        />
                    ))}
                </Box>

                <Divider sx={{ my: tokens.spacing[4] }} />
                <Typography variant="overline" display="block" gutterBottom sx={{ color: tokens.colors.text.secondary, fontSize: tokens.typography.fontSizes.xs }}>{t('settingsPanel.design.saveScheme')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
                    <TextField
                        label={t('settingsPanel.design.schemeName')}
                        variant="outlined"
                        size="small"
                        value={saveSchemeName}
                        onChange={(e) => setSaveSchemeName(e.target.value)}
                        sx={{ flexGrow: 1 }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveScheme}
                        disabled={!saveSchemeName.trim()}
                        sx={{
                          backgroundColor: tokens.colors.primary.main,
                          color: tokens.colors.primary.contrast,
                          padding: `${tokens.spacing[1.5]} ${tokens.spacing[3]}`,
                          borderRadius: tokens.radius.sm,
                          '&:hover': {
                            backgroundColor: tokens.colors.primary.dark,
                          },
                          '&.Mui-disabled': {
                            backgroundColor: tokens.colors.neutral[200],
                            color: tokens.colors.neutral[500],
                          }
                        }}
                    >
                        {t('actions.save')}
                    </Button>
                </Box>
            </Box>
          </Collapse>
        </Box>
      )}
      {/* --- End Customization Controls --- */}


      {/* --- Saved Schemes Section (Conditional & Collapsible) --- */}
      {savedCustomSchemes.length > 0 && (
        <Box sx={{ mb: tokens.spacing[6] }}>
          {/* Use the shared component */}
          <CollapsibleSectionHeader
            title={t('settingsPanel.design.savedSchemes')}
            isOpen={savedSchemesOpen}
            toggleOpen={() => setSavedSchemesOpen(!savedSchemesOpen)}
          />
          <Collapse in={savedSchemesOpen}>
            <Grid container spacing={tokens.spacing[4]} sx={{ pt: tokens.spacing[2] }}>
              {savedCustomSchemes.map((scheme) => (
                <Grid item xs={6} sm={4} key={scheme.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: tokens.radius.base, borderColor: tokens.colors.borders.default }}>
                    <CardContent sx={{ textAlign: 'center', p: tokens.spacing[2], flexGrow: 1 }}>
                      <ColorDonut colors={scheme.colors} size={40} />
                      <Typography variant="body2" sx={{ mt: tokens.spacing[1], fontSize: tokens.typography.fontSizes.sm, color: tokens.colors.text.primary }} noWrap title={scheme.name}>
                        {scheme.name}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', p: tokens.spacing[1], borderTop: `1px solid ${tokens.colors.borders.default}` }}>
                      <Button
                        size="small"
                        onClick={() => {
                          loadCustomScheme(scheme.id);
                          if (selectedDesign !== 'custom') {
                            setSelectedDesign('custom');
                          }
                        }}
                        sx={{ color: tokens.colors.primary.main, fontSize: tokens.typography.fontSizes.xs }}
                      >
                        {t('settingsPanel.design.load')}
                      </Button>
                      <IconButton size="small" onClick={() => deleteCustomScheme(scheme.id)} aria-label={`Delete ${scheme.name}`} sx={{ color: tokens.colors.text.secondary }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Box>
      )}
      {/* --- End Saved Schemes Section --- */}

    </Box>
  );
}

const DesignSelector = React.memo(DesignSelectorComponent);

export default DesignSelector; 